import * as styles from "./PriceList.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@frontend/components/atoms/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useArrayField, useField } from "@frontend/hooks/form";
import type { ArrayItemField } from "@frontend/lib/form";
import { Trans, useLingui } from "@lingui/react/macro";

import type { PriceTypeRow } from "../services";
import { conditionsAtom, conditionsForm } from "../stores/conditions";
import { SectionFrame } from "./Section";

const fields = conditionsForm.fields;

type PriceItem = ArrayItemField<typeof fields.prices>;

/** `Frs / hour`, the two units a line of the price list reads in. */
function unitOf(line: PriceTypeRow | undefined): string {
  return undefined === line
    ? ""
    : [line.priceUnitLabel, line.unitLabel].filter(Boolean).join(" / ");
}

/**
 * The prices this client was granted, one line per kind of work. What is not
 * listed follows the house price, which the popover behind the legend shows.
 */
export function PriceList(): JSX.Element {
  const { t } = useLingui();

  const rows = useArrayField(fields.prices);
  const { options } = useAtomValue(conditionsAtom);

  return (
    <SectionFrame
      icon="Tag"
      legend={t`Price list`}
      action={<PriceListPopover />}
    >
      <p className={styles.hint}>
        <Trans>
          Only the lines this client was granted a price of its own for. The
          rest follow the house price.
        </Trans>
      </p>

      <div className={styles.list}>
        {rows.isEmpty ? (
          <p
            className={styles.empty}
          >{t`This client follows every house price.`}</p>
        ) : null}

        {rows.items.map((item, index) => (
          <PriceRow
            key={item.path}
            item={item}
            lines={options.priceTypes}
            onRemove={() => rows.remove(rows.itemAtoms[index]!)}
          />
        ))}

        {/* The same kind of work can be priced twice, which the record has
            always allowed: nothing is taken off the picker. */}
        <Button
          variant="link"
          size="sm"
          disabled={0 === options.priceTypes.length}
          leftSection={<Icon name="Plus" size="sm" />}
          onClick={() =>
            rows.append({
              priceTypeId: options.priceTypes[0]?.id ?? 0,
              price: null,
            })
          }
        >
          {t`Add a price`}
        </Button>
      </div>
    </SectionFrame>
  );
}

type PriceRowProps = {
  item: PriceItem;
  lines: PriceTypeRow[];
  onRemove: () => void;
};

function PriceRow({ item, lines, onRemove }: PriceRowProps): JSX.Element {
  const { t } = useLingui();

  const priceTypeId = useField(item.fields.priceTypeId);
  const price = useField(item.fields.price);

  const mine = lines.find((line) => line.id === priceTypeId.value);

  return (
    <div className={styles.row}>
      <Button
        variant="ghost"
        size="sm"
        className={styles.remove}
        aria-label={t`Remove`}
        onClick={onRemove}
      >
        <Icon name="CircleMinus" size="sm" />
      </Button>

      <Select
        items={lines.map((line) => ({
          label: line.label,
          value: String(line.id),
        }))}
        value={null == priceTypeId.value ? null : String(priceTypeId.value)}
        size="sm"
        onValueChange={(next) =>
          priceTypeId.setValue(null === next ? 0 : Number(next))
        }
      >
        <SelectTrigger>
          <SelectValue placeholder={t`Please choose an option`} />
        </SelectTrigger>

        <SelectContent>
          {lines.map((line) => (
            <SelectItem key={line.id} value={String(line.id)}>
              {line.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        value={price.value ?? ""}
        size="sm"
        invalid={Boolean(price.error)}
        rightSection={<span className={styles.unit}>{unitOf(mine)}</span>}
        onValueChange={(next) => price.setValue("" === next ? null : next)}
      />
    </div>
  );
}

/**
 * The whole price list as it applies to this client, the counterpart of the
 * legacy `displaypricelist.html.twig`: the house price struck through where
 * the client was granted another one.
 */
function PriceListPopover(): JSX.Element {
  const { t } = useLingui();

  const { conditions, options } = useAtomValue(conditionsAtom);
  const values = useAtomValue(conditionsForm.state.values);

  // The first row of a kind is the one that applies, as the record reads it.
  const granted = new Map<number, string>();

  for (const price of values.prices) {
    const amount = price.price;

    if (null !== amount && "" !== amount.trim()) {
      granted.set(price.priceTypeId, granted.get(price.priceTypeId) ?? amount);
    }
  }

  const house = conditions?.housePrices ?? [];

  // A price granted on a kind of work the house does not price shows under it.
  const extra = [...granted.keys()].filter(
    (priceTypeId) => !house.some((line) => line.priceTypeId === priceTypeId),
  );

  const unitOfId = (priceTypeId: number): string =>
    unitOf(options.priceTypes.find((line) => line.id === priceTypeId));

  const labelOfId = (priceTypeId: number): string =>
    options.priceTypes.find((line) => line.id === priceTypeId)?.label ??
    String(priceTypeId);

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button variant="ghost" size="sm" aria-label={t`Client price list`}>
            <Icon name="Info" size="sm" />
          </Button>
        }
      />

      <PopoverContent side="left">
        <PopoverTitle>{t`Client price list`}</PopoverTitle>

        <div className={styles.popup}>
          {house.map((line) => (
            <PopoverLine
              key={line.priceTypeId}
              label={line.priceTypeLabel}
              house={line.price}
              own={granted.get(line.priceTypeId) ?? null}
              unit={unitOf(
                options.priceTypes.find(
                  (priceType) => priceType.id === line.priceTypeId,
                ),
              )}
            />
          ))}

          {extra.map((priceTypeId) => (
            <PopoverLine
              key={priceTypeId}
              label={labelOfId(priceTypeId)}
              house={null}
              own={granted.get(priceTypeId) ?? null}
              unit={unitOfId(priceTypeId)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type PopoverLineProps = {
  label: string;
  house: string | null;
  own: string | null;
  unit: string;
};

function PopoverLine({
  label,
  house,
  own,
  unit,
}: PopoverLineProps): JSX.Element {
  return (
    <>
      <span className={styles.popupLabel}>{label}</span>

      <span className={styles.popupPrice}>
        {null === own || null === house ? null : (
          <span className={styles.overridden}>({house})</span>
        )}

        <span className={styles.applied}>{own ?? house}</span>

        <span className={styles.unit}>{unit}</span>
      </span>
    </>
  );
}
